import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

import { Grid, Button, Dialog, DialogContent, Typography, IconButton } from "@mui/material";

import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";

import { Props, State } from "./interfaces";
import { Title, Actions } from "./styles";

const DialogWithConfirmationChangesDialog = ({
  openButton,
  title,
  additionalActionButtonsInTitle,
  additionalActionButtonsInFooter,
  customActions,
  hasChanges,
  handleDiscardChanges,
  children,
  forceCloseMainDialog = false,
  resetForceCloseMainDialog,
  maxWidth = undefined,
  closeAfterSave = false,
  seCloseAfterSave,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [state, setState] = useState<State>({
    mainDialogOpen: false,
    confirmationChangesDialogOpen: false,
  });

  const setMainDialogOpen = (value: boolean) => {
    resetForceCloseMainDialog && resetForceCloseMainDialog();
    setState((state) => ({ ...state, mainDialogOpen: value }));
  };

  const setConfirmationChangesDialogOpen = (value: boolean) =>
    setState((state) => ({ ...state, confirmationChangesDialogOpen: value }));

  const closeMainDialog = () => {
    hasChanges ? setConfirmationChangesDialogOpen(true) : setMainDialogOpen(false);
  };

  const discardChanges = () => {
    handleDiscardChanges && handleDiscardChanges();

    setState(() => ({
      mainDialogOpen: false,
      confirmationChangesDialogOpen: false,
    }));
  };

  useEffect(() => {
    forceCloseMainDialog && setMainDialogOpen(false);
  }, [forceCloseMainDialog]);

  useEffect(() => {
    if (closeAfterSave) {
      setMainDialogOpen(false);
      seCloseAfterSave && seCloseAfterSave();
    }
  }, [closeAfterSave]);

  return (
    <>
      <Grid onClick={() => !disabled && setMainDialogOpen(true)}>{openButton}</Grid>
      <Dialog
        fullWidth
        open={state.mainDialogOpen}
        onClose={closeMainDialog}
        maxWidth={maxWidth}
      >
        <Title>
          <Typography
            variant="h6"
            component="span"
          >
            {title}
          </Typography>
          <Grid>
            {additionalActionButtonsInTitle && additionalActionButtonsInTitle}
            <IconButton
              onClick={closeMainDialog}
              style={{ color: theme.palette.error.main }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Grid>
        </Title>
        <DialogContent>{children}</DialogContent>
        <Actions>
          {customActions ? (
            customActions
          ) : (
            <>
              <Button onClick={closeMainDialog}>{`${t("buttons.close")}`}</Button>
              {additionalActionButtonsInFooter && additionalActionButtonsInFooter}
            </>
          )}
        </Actions>
      </Dialog>
      <ConfirmationChangesDialog
        open={state.confirmationChangesDialogOpen}
        handleCancel={() => setConfirmationChangesDialogOpen(false)}
        handleDiscard={discardChanges}
      />
    </>
  );
};

export default DialogWithConfirmationChangesDialog;
